import React from "react";
import { Form, Row, Col, Upload } from "antd";
import { ColorPicker } from "antd5";
import {
  PictureOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  FormItem,
  InputItem,
  TextAreaItem,
  SelectItem,
  SwitchItem,
  ButtonItem,
} from "../../../../Components/Items";
import DynamicCardForm from "../../../../Components/DynamicCardForm";
import { COMPONENTES_CONFIG } from "./componentesConfig";

// Renderiza un campo individual del schema. `parentName` es el prefijo de
// nombre que provee el contenedor (Form.List padre o la zona).
const renderField = (field, parentName, ctx) => {
  const key =
    [...parentName, field.name || field.type].join(".") +
    (field.label ? "::" + field.label : "");

  switch (field.type) {
    case "input":
      return (
        <FormItem
          key={key}
          label={field.label}
          name={[...parentName, field.name]}
          tooltip={field.tooltip}
          required={field.required}
        >
          <InputItem placeholder={field.placeholder} style={field.style} />
        </FormItem>
      );

    case "number":
      return (
        <FormItem
          key={key}
          label={field.label}
          name={[...parentName, field.name]}
          tooltip={field.tooltip}
          required={field.required}
          number
        >
          <InputItem placeholder={field.placeholder} />
        </FormItem>
      );

    case "textarea":
      return (
        <FormItem
          key={key}
          label={field.label}
          name={[...parentName, field.name]}
          tooltip={field.tooltip}
          required={field.required}
          style={{ marginBottom: 0 }}
        >
          <TextAreaItem
            rows={field.rows || 2}
            placeholder={field.placeholder}
          />
        </FormItem>
      );

    case "switch":
      return (
        <Row
          key={key}
          justify="space-between"
          align="middle"
          style={{ marginBottom: 12 }}
        >
          <span style={{ fontWeight: "bold" }}>{field.label}</span>
          <FormItem
            name={[...parentName, field.name]}
            valuePropName="checked"
            margin={false}
          >
            <SwitchItem />
          </FormItem>
        </Row>
      );

    case "select":
      return (
        <FormItem
          key={key}
          label={field.label}
          name={[...parentName, field.name]}
          tooltip={field.tooltip}
          required={field.required}
        >
          <SelectItem
            placeholder={field.placeholder || "Selecciona..."}
            options={field.options}
          />
        </FormItem>
      );

    case "color":
      return (
        <div key={key} style={{ marginBottom: 24 }}>
          <label style={{ display: "block", marginBottom: 8 }}>
            {field.label}
          </label>
          <FormItem
            name={[...parentName, field.name]}
            getValueFromEvent={ctx.normColor}
            margin={false}
            style={{ marginBottom: 0 }}
          >
            <ColorPicker disabledAlpha format="hex" size="small" showText />
          </FormItem>
        </div>
      );

    case "upload":
      return (
        <FormItem
          key={key}
          label={field.label}
          name={[...parentName, field.name]}
          valuePropName="fileList"
          getValueFromEvent={ctx.normFile}
          tooltip={field.tooltip}
          required={field.required}
        >
          <Upload
            listType="picture-card"
            maxCount={1}
            accept="image/*"
            beforeUpload={() => false}
          >
            <div>
              <PictureOutlined />
              <div style={{ marginTop: 8 }}>Subir</div>
            </div>
          </Upload>
        </FormItem>
      );

    case "marcas":
      return (
        <FormItem
          key={key}
          label={field.label}
          name={[...parentName, field.name]}
          tooltip={field.tooltip}
          required={field.required}
        >
          <SelectItem
            mode="multiple"
            allowClear
            placeholder="Buscar y seleccionar marcas..."
            optionFilterProp="children"
            style={{ width: "100%" }}
            options={(ctx.marcasDisponibles || []).map((m) => ({
              value: m._id,
              label: m.nombre,
            }))}
          />
        </FormItem>
      );

    case "group": {
      const cols = field.columns || [];
      const defaultSpan = Math.floor(24 / Math.max(cols.length, 1));
      return (
        <Row key={key} gutter={12} align="bottom">
          {cols.map((col, i) => (
            <Col key={i} span={col.span || defaultSpan}>
              {renderField(col, parentName, ctx)}
            </Col>
          ))}
        </Row>
      );
    }

    case "stringList": {
      const initialValue = field.defaultLength
        ? Array.from({ length: field.defaultLength }, () => "")
        : undefined;
      const minItems = field.min || 0;
      const maxItems = field.max || Infinity;

      return (
        <Form.List
          key={key}
          name={[...parentName, field.name]}
          initialValue={initialValue}
        >
          {(items, { add, remove }) => (
            <div style={{ marginBottom: 16 }}>
              {field.label && (
                <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontWeight: 500,
                  }}
                >
                  {field.label}
                  {field.tooltip && (
                    <span
                      style={{ fontSize: 11, color: "#888", marginLeft: 8 }}
                    >
                      ({field.tooltip})
                    </span>
                  )}
                </label>
              )}
              {items.map((item, idx) => (
                <Row
                  key={item.key}
                  gutter={8}
                  align="middle"
                  style={{ marginBottom: 8 }}
                >
                  <Col flex="auto">
                    <FormItem name={item.name} margin={false}>
                      <InputItem
                        placeholder={
                          field.placeholder ||
                          `${field.itemLabel || "Texto"} ${idx + 1}`
                        }
                      />
                    </FormItem>
                  </Col>
                  <Col>
                    <ButtonItem
                      icon={<DeleteOutlined />}
                      onClick={() => remove(item.name)}
                      disabled={items.length <= minItems}
                      rojo
                      lessTime
                    />
                  </Col>
                </Row>
              ))}
              {items.length < maxItems && (
                <ButtonItem
                  icon={<PlusOutlined />}
                  onClick={() => add("")}
                  cyan
                  style={{ display: "block", margin: "8px 0 0 auto" }}
                >
                  Agregar {field.itemLabel || "Texto"}
                </ButtonItem>
              )}
            </div>
          )}
        </Form.List>
      );
    }

    case "list": {
      const initialValue = field.defaultLength
        ? Array.from({ length: field.defaultLength }, () => ({}))
        : undefined;
      const minItems = field.min || 0;
      const maxItems = field.max || Infinity;

      return (
        <Form.List
          key={key}
          name={[...parentName, field.name]}
          initialValue={initialValue}
        >
          {(items, { add, remove }) => (
            <>
              {items.map((item, idx) => (
                <DynamicCardForm
                  key={item.key}
                  title={`${field.itemLabel || "Item"} ${idx + 1}`}
                  style={{ margin: "0 0 12px 0", maxWidth: "100%" }}
                  headerRight={
                    items.length > minItems ? (
                      <ButtonItem
                        icon={<DeleteOutlined />}
                        onClick={() => remove(item.name)}
                        rojo
                        lessTime
                      />
                    ) : null
                  }
                >
                  {(field.fields || []).map((sub) =>
                    renderField(sub, [item.name], ctx),
                  )}
                </DynamicCardForm>
              ))}

              {items.length < maxItems && (
                <ButtonItem
                  icon={<PlusOutlined />}
                  onClick={() => add({})}
                  cyan
                  style={{ display: "block", margin: "0 0 12px auto" }}
                >
                  Agregar {field.itemLabel || "Item"}
                </ButtonItem>
              )}
            </>
          )}
        </Form.List>
      );
    }

    default:
      return null;
  }
};

const ZonaFieldsRenderer = ({
  tipo,
  variante,
  parentName,
  marcasDisponibles,
  normFile,
  normColor,
}) => {
  const config = COMPONENTES_CONFIG[tipo];
  const fieldsActivos = variante
    ? config?.variantes?.[variante]?.fields ?? []
    : [];

  if (fieldsActivos.length === 0) return null;
  const ctx = { marcasDisponibles, normFile, normColor };

  return (
    <>
      {fieldsActivos.map((f, idx) => (
        <React.Fragment key={`${tipo}-${variante}-${idx}`}>
          {renderField(f, parentName, ctx)}
        </React.Fragment>
      ))}
    </>
  );
};

export default ZonaFieldsRenderer;
